/**
 * P3-40: AchievementsPanel —— 成就面板。
 *
 * 逐结构对齐 `js/achievements.js:524-724`：
 * - 头部显示 ethical 成就完成数 / 总数 + 星标计数
 * - 跳过未解锁的隐藏成就
 * - 每个成就渲染为 span.achievement，class 与 title/content 随 unlocked 变化
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";

interface AchievementModel {
  name: string;
  title: string;
  description: string;
  unlocked: boolean;
  hidden: boolean;
  unethical: boolean;
  starCondition?: unknown;
  starUnlocked?: boolean;
  starDescription?: string;
}

interface AchievementPanelSnapshot {
  header: string;
  starText: string;
  achievements: AchievementModel[];
}

function getAchievementsSnapshot(game: GamePage | null): AchievementPanelSnapshot | null {
  if (!game) {
    return null;
  }

  const achievements = (game.achievements?.achievements as AchievementModel[] | undefined) ?? [];
  let totalAchievements = 0;
  let completedAchievements = 0;
  let completedStars = 0;
  let uncompletedStars = 0;

  const visible: AchievementModel[] = [];

  for (const ach of achievements) {
    if (!ach.unlocked && ach.hidden) {
      continue;
    }

    if (!ach.unethical) {
      totalAchievements++;
      if (ach.unlocked) {
        completedAchievements++;
      }
    }

    visible.push(ach);

    if (ach.starCondition === undefined) {
      continue;
    }

    if (!ach.unethical) {
      if (ach.starUnlocked) {
        completedStars++;
      } else {
        uncompletedStars++;
      }
    }
  }

  const header = t(
    "achievements.header",
    [completedAchievements, totalAchievements],
  ) ?? `achievements.header ${completedAchievements} ${totalAchievements}`;

  const starText = "★".repeat(completedStars) + "☆".repeat(uncompletedStars);

  return { header, starText, achievements: visible };
}

function snapEq(a: AchievementPanelSnapshot | null, b: AchievementPanelSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.header !== b.header || a.starText !== b.starText || a.achievements.length !== b.achievements.length) {
    return false;
  }
  for (let i = 0; i < a.achievements.length; i++) {
    const aa = a.achievements[i];
    const bb = b.achievements[i];
    if (
      aa.name !== bb.name ||
      aa.title !== bb.title ||
      aa.description !== bb.description ||
      aa.unlocked !== bb.unlocked ||
      aa.hidden !== bb.hidden ||
      aa.unethical !== bb.unethical ||
      aa.starCondition !== bb.starCondition ||
      aa.starUnlocked !== bb.starUnlocked ||
      aa.starDescription !== bb.starDescription
    ) {
      return false;
    }
  }
  return true;
}

export default function AchievementsPanel() {
  const snapshot = useGameTick(
    (game) => getAchievementsSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot) {
    return null;
  }

  return (
    <div>
      <div className="achievement-header">
        {snapshot.header}
        <span className="star" dangerouslySetInnerHTML={{ __html: snapshot.starText }} />
      </div>
      <div>
        {snapshot.achievements.map((ach) => {
          const className =
            "achievement" +
            (ach.unlocked && ach.unethical ? " unethical" : "") +
            (ach.unlocked ? " unlocked" : "") +
            (ach.starUnlocked ? " starUnlocked" : "");
          const title = ach.unlocked ? ach.description : "???";
          const content = ach.unlocked ? ach.title : "???";

          return (
            <span
              key={ach.name}
              className={className}
              title={title}
            >
              <span dangerouslySetInnerHTML={{ __html: content }} />
              {ach.starCondition !== undefined && (
                <div
                  className="star"
                  title={ach.starUnlocked ? ach.starDescription : "???"}
                  dangerouslySetInnerHTML={{
                    __html: ach.starUnlocked ? "★" : "☆",
                  }}
                />
              )}
            </span>
          );
        })}
      </div>
    </div>
  );
}
